import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Calendar, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Plus,
  Trash2,
  ArrowLeft,
  Mail,
  Download,
  Upload,
  Settings,
  Clock,
  Users,
  Bell,
  Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function CalendarManager({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [counts, setCounts] = useState({ outlook: 0, google: 0, outlook_max: 3, google_max: 3 });
  const [syncing, setSyncing] = useState(false);
  
  // Player preferences
  const [preferences, setPreferences] = useState({
    auto_exclude_when_busy: false,
    auto_add_team_games: false,
    push_calendar_id: null,
    push_calendar_email: null,
    teams: []
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    fetchConnections();
    fetchPreferences();
    
    // Check for success/error from callback
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const provider = searchParams.get("provider");
    const email = searchParams.get("email");
    
    if (success === "true") {
      toast.success(`Connected ${provider === "google" ? "Google" : "Outlook"} calendar: ${email}`);
      // Clear URL params
      window.history.replaceState({}, "", "/outlook-calendar");
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
      window.history.replaceState({}, "", "/outlook-calendar");
    }
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/api/calendar-preferences/my-preferences`, {
        withCredentials: true
      });
      setPreferences(response.data);
    } catch (error) {
      console.error("Error fetching preferences:", error);
    }
  };

  const updatePreferences = async (updates) => {
    try {
      setSavingPrefs(true);
      const newPrefs = { ...preferences, ...updates };
      await axios.put(`${API}/api/calendar-preferences/my-preferences`, {
        auto_exclude_when_busy: newPrefs.auto_exclude_when_busy,
        auto_add_team_games: newPrefs.auto_add_team_games,
        push_calendar_id: newPrefs.push_calendar_id
      }, { withCredentials: true });
      
      setPreferences(newPrefs);
      toast.success("Preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/calendars/connections`, {
        withCredentials: true
      });
      setConnections(response.data.connections || []);
      setCounts(response.data.counts || { outlook: 0, google: 0, outlook_max: 3, google_max: 3 });
    } catch (error) {
      console.error("Error fetching connections:", error);
      toast.error("Failed to load calendar connections");
    } finally {
      setLoading(false);
    }
  };

  const connectOutlook = async () => {
    if (counts.outlook >= counts.outlook_max) {
      toast.error(`Maximum ${counts.outlook_max} Outlook calendars allowed`);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/calendars/connect/outlook`, {
        withCredentials: true
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Outlook");
      setLoading(false);
    }
  };

  const connectGoogle = async () => {
    if (counts.google >= counts.google_max) {
      toast.error(`Maximum ${counts.google_max} Google calendars allowed`);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/calendars/connect/google`, {
        withCredentials: true
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Google");
      setLoading(false);
    }
  };

  const updateSettings = async (calendarId, syncIncoming, syncOutgoing) => {
    try {
      await axios.patch(`${API}/api/calendars/connection/${calendarId}/settings`, {
        sync_incoming: syncIncoming,
        sync_outgoing: syncOutgoing
      }, { withCredentials: true });
      
      // Update local state
      setConnections(prev => prev.map(c => 
        c.id === calendarId 
          ? { ...c, sync_incoming: syncIncoming, sync_outgoing: syncOutgoing }
          : c
      ));
      
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const disconnectCalendar = async (calendarId, email) => {
    if (!window.confirm(`Disconnect ${email}?`)) return;
    
    try {
      await axios.delete(`${API}/api/calendars/connection/${calendarId}`, {
        withCredentials: true
      });
      toast.success(`Disconnected ${email}`);
      fetchConnections();
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  const syncAllCalendars = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/api/calendars/sync-all`, {}, {
        withCredentials: true
      });
      toast.success(`Synced ${response.data.busy_blocks_count} busy blocks`);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString('en-SG', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && connections.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Card className="p-6">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
              <p className="text-slate-400">Loading calendars...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-slate-400 hover:text-white -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/committee-availability")}
            className="text-yellow-500 hover:text-yellow-400"
          >
            <Users className="w-4 h-4 mr-2" />
            Committee Availability
          </Button>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Calendar className="w-8 h-8 mr-3 text-yellow-500" />
              Calendar Sync
            </h1>
            <p className="text-slate-400 mt-2">
              Connect multiple calendars to manage your availability
            </p>
          </div>
          
          {connections.length > 0 && (
            <Button
              onClick={syncAllCalendars}
              disabled={syncing}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {syncing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync All
            </Button>
          )}
        </div>

        {/* Add Calendar Buttons */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card 
            className={`p-4 cursor-pointer transition-all hover:border-blue-500/50 ${
              counts.outlook >= counts.outlook_max ? 'opacity-50' : ''
            }`}
            onClick={counts.outlook < counts.outlook_max ? connectOutlook : undefined}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white">Add Outlook</h3>
                <p className="text-xs text-slate-400">{counts.outlook}/{counts.outlook_max} connected</p>
              </div>
              <Plus className="w-5 h-5 text-slate-400" />
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all hover:border-red-500/50 ${
              counts.google >= counts.google_max ? 'opacity-50' : ''
            }`}
            onClick={counts.google < counts.google_max ? connectGoogle : undefined}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
                  <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
                  <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
                  <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white">Add Google</h3>
                <p className="text-xs text-slate-400">{counts.google}/{counts.google_max} connected</p>
              </div>
              <Plus className="w-5 h-5 text-slate-400" />
            </div>
          </Card>
        </div>

        {/* Connected Calendars */}
        {connections.length === 0 ? (
          <Card className="p-8 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <h3 className="text-lg font-bold text-white mb-2">No Calendars Connected</h3>
            <p className="text-slate-400 mb-4">
              Connect your Outlook or Google calendars to sync your availability
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center">
              <Settings className="w-5 h-5 mr-2 text-slate-400" />
              Connected Calendars ({connections.length})
            </h2>
            
            {connections.map((calendar) => (
              <Card key={calendar.id} className="p-4">
                <div className="flex items-start gap-4">
                  {/* Provider Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    calendar.provider === 'outlook' ? 'bg-blue-500/20' : 'bg-red-500/20'
                  }`}>
                    {calendar.provider === 'outlook' ? (
                      <Mail className="w-6 h-6 text-blue-400" />
                    ) : (
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
                        <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
                        <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
                        <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
                      </svg>
                    )}
                  </div>
                  
                  {/* Calendar Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white">{calendar.email}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        calendar.provider === 'outlook' ? 'bg-blue-500/20 text-blue-300' : 'bg-red-500/20 text-red-300'
                      }`}>
                        {calendar.provider === 'outlook' ? 'Outlook' : 'Google'}
                      </span>
                    </div>
                    
                    <p className="text-xs text-slate-400 mb-3">
                      Connected {formatDate(calendar.connected_at)} • Last synced: {formatDate(calendar.last_synced)}
                    </p>
                    
                    {/* Sync Options */}
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                          checked={calendar.sync_incoming}
                          onCheckedChange={(checked) => updateSettings(calendar.id, checked, calendar.sync_outgoing)}
                        />
                        <span className="text-sm text-slate-300 flex items-center">
                          <Download className="w-3 h-3 mr-1 text-green-400" />
                          Pull availability
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                          checked={calendar.sync_outgoing}
                          onCheckedChange={(checked) => updateSettings(calendar.id, calendar.sync_incoming, checked)}
                        />
                        <span className="text-sm text-slate-300 flex items-center">
                          <Upload className="w-3 h-3 mr-1 text-blue-400" />
                          Push games
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectCalendar(calendar.id, calendar.email)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Player Game Sync Preferences */}
        {connections.length > 0 && (
          <Card className="p-6 mt-6 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/30">
            <h3 className="font-bold text-white mb-4 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-purple-400" />
              Game Sync Settings
            </h3>
            
            {preferences.teams && preferences.teams.length > 0 && (
              <p className="text-sm text-slate-400 mb-4">
                Your teams: <span className="text-white font-medium">{preferences.teams.join(', ')}</span>
              </p>
            )}
            
            <div className="space-y-4">
              {/* Auto-exclude when busy */}
              <div className="flex items-start justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-400" />
                    <p className="text-white font-medium">Auto-exclude me from games when busy</p>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    If your calendar shows you're busy during a game time, you'll be automatically marked as unavailable for squad selection
                  </p>
                </div>
                <Switch
                  checked={preferences.auto_exclude_when_busy}
                  onCheckedChange={(checked) => updatePreferences({ auto_exclude_when_busy: checked })}
                  disabled={savingPrefs}
                />
              </div>
              
              {/* Auto-add team games */}
              <div className="flex items-start justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-green-400" />
                    <p className="text-white font-medium">Auto-add my team's games to calendar</p>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Games for your teams ({preferences.teams?.join(', ') || 'none assigned'}) will be automatically added to your calendar
                  </p>
                </div>
                <Switch
                  checked={preferences.auto_add_team_games}
                  onCheckedChange={(checked) => updatePreferences({ auto_add_team_games: checked })}
                  disabled={savingPrefs}
                />
              </div>
              
              {/* Calendar selection for pushing games */}
              {preferences.auto_add_team_games && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-white font-medium mb-2">Push games to:</p>
                  <select
                    value={preferences.push_calendar_id || ''}
                    onChange={(e) => updatePreferences({ push_calendar_id: e.target.value || null })}
                    className="w-full p-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                    disabled={savingPrefs}
                  >
                    <option value="">Select a calendar...</option>
                    {connections.map(cal => (
                      <option key={cal.id} value={cal.id}>
                        {cal.email} ({cal.provider === 'outlook' ? 'Outlook' : 'Google'})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Legend */}
        {connections.length > 0 && (
          <Card className="p-4 mt-6 bg-slate-800/50">
            <h3 className="font-bold text-white mb-3 text-sm">Sync Options Explained</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Download className="w-4 h-4 text-green-400 mt-0.5" />
                <div>
                  <p className="text-slate-200 font-medium">Pull Availability</p>
                  <p className="text-slate-400 text-xs">Import busy times from this calendar so others know when you're unavailable</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Upload className="w-4 h-4 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-slate-200 font-medium">Push Games</p>
                  <p className="text-slate-400 text-xs">Export SCC games you're selected for to this calendar</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Meeting Finder Link */}
        <Card className="p-4 mt-6 bg-amber-500/10 border-amber-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-amber-500" />
              <div>
                <h3 className="font-bold text-white">Find Meeting Time</h3>
                <p className="text-sm text-slate-400">Find when committee members are available</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/meeting-finder")}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Open Finder
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
